import { METHOD_QUERY_KEY } from '@/components/admin/reglages/method-editor';
import { useAdminSession } from '@/lib/admin-session';
import { fetchMethod } from '@/lib/api';
import type { MethodContent } from '@matter_extractor/shared';
import { METHOD_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';

/*
 * The key Méthode is read under (analyste.md §4, issue #196) is the one the editor of
 * Réglages > Méthode writes its detail under - IMPORTED from it, as
 * `lib/use-noyau-texts.ts` does for the texts of the noyaux, and not spelled a second
 * time: two literals for one singleton drifted apart once, and a version saved in the
 * tab then showed in the panels of a protocol only when they next refetched.
 */

/**
 * What the panels « Ce que la passe N recevra » know of Méthode (§3.4):
 *
 * - `reading` - the detail has not landed yet: nothing is claimed;
 * - `missing` - a 404, a store with no version of Méthode on this side (a vitrine
 *   before the first « Passer en production », §4.5): the panels say so and break
 *   nothing, since the editor of a protocol is as usable there as anywhere;
 * - `failed` - the read failed (a 500, the network, an answer that is not a Méthode):
 *   the panels say that the READ failed - not that Méthode has no version, which
 *   nothing has told them - and break nothing either, the editor being as usable
 *   with the part of Méthode out of sight as without a version of it;
 * - `read` - the « latest dev » version, which the panels render and name.
 *
 * A read that fails AFTER one that landed is not `failed`: the detail the query
 * kept is the latest dev as it was a moment ago, and a refetch that stumbles is no
 * reason to take the part away from the screen.
 */
export type MethodSource =
  | { readonly state: 'reading' }
  | { readonly state: 'missing' }
  | { readonly state: 'failed' }
  | {
      readonly state: 'read';
      /** The version number of the development pointer: « Réglages > Méthode v2 ». */
      readonly number: number;
      readonly content: MethodContent;
    };

/**
 * Méthode as the section Analyste reads it: the development version, the one an
 * operator is tuning (§3.4, « la latest dev de Méthode »). One request, fired when
 * the section MOUNTS - the editor keeps every view mounted, so it leaves even
 * when the section is hidden, and also on a protocol that does not exist yet.
 */
export function useMethodSource(): MethodSource {
  const { email } = useAdminSession();
  const detail = useQuery({
    queryKey: [METHOD_QUERY_KEY, email, METHOD_ID],
    queryFn: fetchMethod,
    retry: false,
  });
  if (detail.data === undefined) return { state: detail.isError ? 'failed' : 'reading' };
  if (detail.data === null) return { state: 'missing' };
  return {
    state: 'read',
    number: detail.data.development.number,
    content: detail.data.developmentContent,
  };
}
